/**
 * Vendor directory service.
 *
 * Default path (USE_MOCKS=false): real apiClient calls into the
 * `/vendors/*` API routes wired in `infra/apigateway-vendors.ts`.
 *
 * Mock path (USE_MOCKS=true): in-memory store keyed by propertyId,
 * seeded from `./mocks.ts`. Useful for local demo / Storybook /
 * Chromatic snapshots where you don't want to hit a deployed API.
 *
 * The discovery routes (`search`, `auto-populate`, `auto-populate-all`)
 * are wired but the BE currently returns `hauser_eq_pending: true` and
 * an empty payload pending the Phase 2 hauser-eq integration (extends
 * the `google-places-data` module with trade-based search). The FE
 * tolerates the empty response.
 */
import { MOCK_CANDIDATES_BY_TRADE, MOCK_SAVED_VENDORS } from './mocks';

import type {
  CreateVendorInput,
  SearchVendorCandidatesInput,
  UpdateVendorInput,
  Vendor,
  VendorCandidate,
} from '@/models/Vendor';

import { apiClient } from '@/services/api';
import { logger } from '@/utils/logger/loggerInstance';

const USE_MOCKS = false;

/**
 * Auto-pick eligibility bar. A vendor must meet ALL three to be a
 * candidate for the "Curate Top 3" action. Among those that qualify,
 * the BE ranks by `rating × reviewCount` and returns the top N.
 *
 * Mirrors `AUTO_PICK_CRITERIA` in
 * `packages/functions/common/models/Vendor.ts`. Keep in sync — the
 * tooltip copy and the actual server-side filter should not drift.
 */
export const AUTO_PICK_CRITERIA = {
  minRating: 4.5,
  minReviewCount: 200,
} as const;

// ─── Mock store ─────────────────────────────────────────────────────

const inMemoryVendors = new Map<string, Vendor[]>();

const seedForProperty = (propertyId: string): Vendor[] => {
  const existing = inMemoryVendors.get(propertyId);
  if (existing) return existing;
  const seeded = MOCK_SAVED_VENDORS.map((v) => ({ ...v, propertyId }));
  inMemoryVendors.set(propertyId, seeded);
  return seeded;
};

const pickTopForTrade = (
  trade: SearchVendorCandidatesInput['trade'],
  count: number,
  excludePlaceIds: Set<string>,
) => {
  const eligible = (MOCK_CANDIDATES_BY_TRADE[trade] ?? []).filter((c) => {
    if (c.place_id && excludePlaceIds.has(c.place_id)) return false;
    if ((c.rating ?? 0) < AUTO_PICK_CRITERIA.minRating) return false;
    if ((c.reviewCount ?? 0) < AUTO_PICK_CRITERIA.minReviewCount) return false;
    return true;
  });
  return eligible
    .slice()
    .sort((a, b) => (b.rating ?? 0) * (b.reviewCount ?? 0) - (a.rating ?? 0) * (a.reviewCount ?? 0))
    .slice(0, count);
};

const candidateToVendor = (c: VendorCandidate, propertyId: string): Vendor => ({
  ...c,
  id: `v-${Math.random().toString(36).slice(2, 10)}`,
  propertyId,
  pinned: true,
  createdAt: Date.now(),
  updatedAt: Date.now(),
});

// ─── Real API calls ─────────────────────────────────────────────────

export const getPropertyVendors = async (propertyId?: string): Promise<Vendor[]> => {
  if (!propertyId) return [];

  if (USE_MOCKS) {
    logger.debug(`[mock] GET /properties/${propertyId}/vendors`);
    return seedForProperty(propertyId);
  }

  const url = `/properties/${propertyId}/vendors`;
  logger.debug(`GET ${url}`);
  const response = await apiClient.get<Vendor[]>(url);
  return response.data;
};

export const createVendor = async ({
  propertyId,
  data,
}: {
  propertyId: string;
  data: CreateVendorInput;
}): Promise<Vendor> => {
  if (USE_MOCKS) {
    logger.debug(`[mock] POST /vendors`, data);
    const list = seedForProperty(propertyId);
    const vendor: Vendor = {
      ...data,
      id: `v-${Math.random().toString(36).slice(2, 10)}`,
      propertyId,
      pinned: true,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    list.push(vendor);
    return vendor;
  }

  const url = '/vendors';
  logger.debug(`POST ${url}`);
  const response = await apiClient.post<Vendor>(url, { ...data, propertyId });
  return response.data;
};

export const updateVendor = async ({
  id,
  propertyId,
  data,
}: {
  id: string;
  propertyId: string;
  data: UpdateVendorInput;
}): Promise<Vendor> => {
  if (USE_MOCKS) {
    logger.debug(`[mock] PUT /vendors/${id}`, data);
    const list = seedForProperty(propertyId);
    const idx = list.findIndex((v) => v.id === id);
    if (idx === -1) throw new Error(`vendor ${id} not found`);
    list[idx] = { ...list[idx], ...data, updatedAt: Date.now() };
    return list[idx];
  }

  const url = `/vendors/${id}`;
  logger.debug(`PUT ${url}`);
  const response = await apiClient.put<Vendor>(url, data);
  return response.data;
};

export const deleteVendor = async ({
  id,
  propertyId,
}: {
  id: string;
  propertyId: string;
}): Promise<void> => {
  if (USE_MOCKS) {
    logger.debug(`[mock] DELETE /vendors/${id}`);
    const list = seedForProperty(propertyId);
    const idx = list.findIndex((v) => v.id === id);
    if (idx !== -1) list.splice(idx, 1);
    return;
  }

  const url = `/vendors/${id}`;
  logger.debug(`DELETE ${url}`);
  await apiClient.delete(url);
};

/**
 * Discovery — currently returns `{ candidates: [], hauser_eq_pending: true }`
 * until Phase 2 lands. FE consumers tolerate empty results gracefully.
 */
type SearchResponse = {
  candidates: VendorCandidate[];
  hauser_eq_pending?: boolean;
};

export const searchVendorCandidates = async (
  input: SearchVendorCandidatesInput,
): Promise<VendorCandidate[]> => {
  if (USE_MOCKS) {
    logger.debug(`[mock] GET /vendors/search`, input);
    const all = MOCK_CANDIDATES_BY_TRADE[input.trade] ?? [];
    const f = input.filters ?? {};
    return all.filter((c) => {
      if (f.minRating != null && (c.rating ?? 0) < f.minRating) return false;
      if (f.minReviewCount != null && (c.reviewCount ?? 0) < f.minReviewCount) return false;
      if (f.maxPriceLevel != null && (c.priceLevel ?? 99) > f.maxPriceLevel) return false;
      if (
        f.maxDistanceMi != null &&
        c.distance &&
        c.distance.unit === 'mi' &&
        c.distance.value > f.maxDistanceMi
      )
        return false;
      return true;
    });
  }

  const params = new URLSearchParams({
    propertyId: input.propertyId,
    trade: input.trade,
  });
  if (input.textQuery) params.set('textQuery', input.textQuery);
  for (const [k, v] of Object.entries(input.filters ?? {})) {
    if (v != null) params.set(k, String(v));
  }
  const url = `/vendors/search?${params.toString()}`;
  logger.debug(`GET ${url}`);
  const response = await apiClient.get<SearchResponse>(url);
  if (response.data.hauser_eq_pending) {
    throw new Error('Vendor discovery is still preparing for this property. Please try again.');
  }
  return response.data.candidates;
};

type AutoPopulateResponse = {
  created: Vendor[];
  hauser_eq_pending?: boolean;
};

export const autoPopulateVendors = async ({
  propertyId,
  trade,
  count = 3,
}: {
  propertyId: string;
  trade: SearchVendorCandidatesInput['trade'];
  count?: number;
}): Promise<Vendor[]> => {
  if (USE_MOCKS) {
    logger.debug(`[mock] POST /vendors/auto-populate`, { propertyId, trade, count });
    const list = seedForProperty(propertyId);
    const excludePlaceIds = new Set(
      list.filter((v) => v.place_id).map((v) => v.place_id as string),
    );
    const candidates = pickTopForTrade(trade, count, excludePlaceIds);
    const created = candidates.map((c) => candidateToVendor(c, propertyId));
    list.push(...created);
    return created;
  }

  const url = '/vendors/auto-populate';
  logger.debug(`POST ${url}`);
  const response = await apiClient.post<AutoPopulateResponse>(url, {
    propertyId,
    trade,
    count,
  });
  if (response.data.hauser_eq_pending) {
    throw new Error('Vendor discovery is still preparing for this property. Please try again.');
  }
  return response.data.created;
};

/**
 * Auto-pick Top N across every trade in one call. Skips trades the
 * owner already has saved vendors in (BE-side dedup) and applies the
 * same AUTO_PICK_CRITERIA bar per trade.
 */
export const autoPopulateAllTrades = async ({
  propertyId,
  count = 3,
}: {
  propertyId: string;
  count?: number;
}): Promise<Vendor[]> => {
  if (USE_MOCKS) {
    logger.debug(`[mock] POST /vendors/auto-populate-all`, { propertyId, count });
    const list = seedForProperty(propertyId);
    const excludePlaceIds = new Set(
      list.filter((v) => v.place_id).map((v) => v.place_id as string),
    );
    const trades = Object.keys(MOCK_CANDIDATES_BY_TRADE) as SearchVendorCandidatesInput['trade'][];
    const created: Vendor[] = [];
    for (const trade of trades) {
      const top = pickTopForTrade(trade, count, excludePlaceIds);
      for (const c of top) {
        created.push(candidateToVendor(c, propertyId));
        if (c.place_id) excludePlaceIds.add(c.place_id);
      }
    }
    list.push(...created);
    return created;
  }

  const url = '/vendors/auto-populate-all';
  logger.debug(`POST ${url}`);
  const response = await apiClient.post<AutoPopulateResponse>(url, { propertyId, count });
  if (response.data.hauser_eq_pending) {
    throw new Error('Vendor discovery is still preparing for this property. Please try again.');
  }
  return response.data.created;
};
